// Camera QR scanner (the New device). Reads frames off a live camera and
// decodes with the bundled `jsqr` (no network). Falls back to a manual paste of
// the code shown under the QR — the code still travels device→human→device, out
// of band, never through the relay (docs/specs/chat-device-linking.md §5).

import { type ChangeEvent, useCallback, useEffect, useRef, useState } from 'react';

import jsQR from 'jsqr';

import { Button } from '../ui/Button';

interface Props {
  onResult: (text: string) => void;
}

export function QrScan({ onResult }: Props) {
  const videoRef = useRef<HTMLVideoElement>(null);
  const [manual, setManual] = useState(false);
  const [pasted, setPasted] = useState('');
  const [error, setError] = useState('');

  const onPasteChange = useCallback((e: ChangeEvent<HTMLTextAreaElement>) => setPasted(e.target.value), []);
  const submitPaste = useCallback(() => onResult(pasted.trim()), [onResult, pasted]);
  const useCamera = useCallback(() => setManual(false), []);
  const useManual = useCallback(() => setManual(true), []);

  useEffect(() => {
    if (manual) return;

    let stream: MediaStream | null = null;
    let raf = 0;
    let stopped = false;
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d', { willReadFrequently: true });

    const stop = () => {
      stopped = true;
      cancelAnimationFrame(raf);
      stream?.getTracks().forEach((t) => t.stop());
    };

    const tick = () => {
      const v = videoRef.current;
      if (stopped || !v || !ctx) return;
      if (v.readyState === v.HAVE_ENOUGH_DATA && v.videoWidth > 0) {
        canvas.width = v.videoWidth;
        canvas.height = v.videoHeight;
        ctx.drawImage(v, 0, 0, canvas.width, canvas.height);
        const frame = ctx.getImageData(0, 0, canvas.width, canvas.height);
        const found = jsQR(frame.data, frame.width, frame.height);
        if (found?.data) {
          stop();
          onResult(found.data);
          return;
        }
      }
      raf = requestAnimationFrame(tick);
    };

    navigator.mediaDevices
      .getUserMedia({ video: { facingMode: 'environment' } })
      .then((s) => {
        if (stopped) {
          s.getTracks().forEach((t) => t.stop());
          return;
        }
        stream = s;
        const v = videoRef.current;
        if (v) {
          v.srcObject = s;
          void v.play().catch(() => undefined);
        }
        raf = requestAnimationFrame(tick);
      })
      .catch(() => setError('Camera unavailable — enter the code instead.'));

    return stop;
  }, [manual, onResult]);

  if (manual) {
    return (
      <div className="bz-link-paste">
        <textarea
          className="bz-link-paste-input"
          value={pasted}
          onChange={onPasteChange}
          placeholder="Paste the code shown under the QR on your other device"
          rows={3}
          aria-label="Device-link code"
        />
        <Button variant="primary" block disabled={!pasted.trim()} onClick={submitPaste}>
          Continue
        </Button>
        <Button variant="ghost" block onClick={useCamera}>
          Use the camera instead
        </Button>
      </div>
    );
  }

  return (
    <div className="bz-link-scan">
      <div className="bz-link-viewport">
        <video ref={videoRef} className="bz-link-video" muted playsInline />
        <div className="bz-link-reticle" aria-hidden="true" />
      </div>
      {error && <p className="bz-link-error">{error}</p>}
      <Button variant="ghost" block onClick={useManual}>
        Enter the code manually
      </Button>
    </div>
  );
}
